import React, { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { Button, Field, Notice } from '../../design-system/components';
import { formatCnpj, formatCpf, validCnpj, validCpf } from './helpers';
import type { Client, Proposal } from '../../types/contracts';

const labels: Record<string, string> = {
  sent: 'Registrar envio ao cliente',
  approve: 'Registrar aprovação',
  refuse: 'Registrar recusa',
  negotiation: 'Registrar negociação',
  cancel_approval: 'Cancelar aprovação',
  ready_signature: 'Marcar como apta para assinatura',
  contracted: 'Registrar contratação',
  cancelled: 'Cancelar proposta',
  closed: 'Encerrar proposta',
};
type Modal = { kind: 'management' | 'revision' | 'contractual'; value: string; title: string } | null;
const contactKeys = [
  ['commercial', 'Contato principal'],
  ['technical', 'Interlocutor técnico'],
  ['legal_representative', 'Representante legal'],
  ['signatory', 'Signatário'],
  ['payment', 'Responsável pelo pagamento'],
] as const;

export function ManageProposal({ id, onOpen, onBack }: { id: string; onOpen: (id: string) => void; onBack: () => void }) {
  const [p, setP] = useState<Proposal | null>(null);
  const [client, setClient] = useState<Client | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [modal, setModal] = useState<Modal>(null);
  const [archiving, setArchiving] = useState(false);
  async function load() {
    try {
      const proposal = await api.get<Proposal>(`/proposals/${id}`);
      setP(proposal);
      if (proposal.client_id) setClient(await api.get<Client>(`/clients/${proposal.client_id}`));
    } catch (e: any) {
      setError(e.message);
    }
  }
  useEffect(() => {
    load();
  }, [id]);
  async function toggleArchive() {
    if (!p) return;
    const archived = p.operational_state === 'arquivada';
    if (!archived && !window.confirm('Arquivar esta proposta? Ela sairá da listagem principal, mas seus dados e histórico serão preservados.')) return;
    setArchiving(true);
    setError('');
    try {
      await api.post(`/proposals/${id}/archive`, { archived: !archived });
      if (!archived) {
        onBack();
        return;
      }
      setMessage('Proposta restaurada do arquivo.');
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setArchiving(false);
    }
  }
  async function submitModal(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!modal) return;
    const f = new FormData(e.currentTarget);
    const reason = String(f.get('reason') || '').trim();
    try {
      if (modal.kind === 'management') await api.post(`/proposals/${id}/management`, { action: modal.value, reason, data: {} });
      else if (modal.kind === 'revision') {
        const r = await api.post<{ id: string }>(`/proposals/${id}/revisions`, { reason });
        setModal(null);
        onOpen(r.id);
        return;
      } else {
        const r = await api.post<{ id: string }>(`/proposals/${id}/contractual-version`, {
          reason: reason || 'Formalização após aprovação comercial',
        });
        setModal(null);
        onOpen(r.id);
        return;
      }
      setModal(null);
      setMessage('Estado atualizado.');
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function saveClient(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!client) return;
    const f = new FormData(e.currentTarget);
    const tax = String(f.get('tax_id') || '');
    if (client.person_type === 'PF' && tax && !validCpf(tax)) {
      setError('Informe um CPF válido.');
      return;
    }
    if (client.person_type === 'PJ' && !validCnpj(tax)) {
      setError('Informe um CNPJ válido.');
      return;
    }
    const signatoryTax = String(f.get('signatory_tax_id') || '');
    if (client.person_type === 'PJ' && signatoryTax && !validCpf(signatoryTax)) {
      setError('Informe um CPF válido para o representante signatário ou deixe o campo vazio.');
      return;
    }
    const contacts: any = { ...(client.contacts_json || {}) };
    if (client.person_type === 'PF')
      contacts.primary = {
        name: String(f.get('primary_name') || client.name),
        role: String(f.get('primary_role') || ''),
        email: String(f.get('primary_email') || client.email || ''),
        phone: String(f.get('primary_phone') || client.phone || ''),
      };
    else
      for (const [key] of contactKeys) {
        contacts[key] = {
          name: String(f.get(`${key}_name`) || ''),
          role: String(f.get(`${key}_role`) || ''),
          email: String(f.get(`${key}_email`) || ''),
          phone: String(f.get(`${key}_phone`) || ''),
          ...(key === 'signatory' ? { tax_id: signatoryTax } : {}),
        };
      }
    try {
      await api.patch(`/clients/${client.id}`, {
        tax_id: tax,
        address_json: {
          street: String(f.get('street') || ''),
          number: String(f.get('number') || ''),
          city: String(f.get('city') || ''),
          state: String(f.get('state') || ''),
        },
        contacts_json: contacts,
        email: String(f.get('email') || client.email || ''),
        phone: String(f.get('phone') || client.phone || ''),
      });
      setMessage('Dados do cliente atualizados.');
      setError('');
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function signed(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const file = f.get('signed');
    if (!(file instanceof File) || !file.size) {
      setError('Selecione um PDF assinado.');
      return;
    }
    const body = new FormData();
    body.append('file', file);
    try {
      await api.upload(`/proposals/${id}/signed-pdf`, body);
      setMessage('Documento assinado registrado.');
      await load();
    } catch (e: any) {
      setError(e.message);
    }
  }
  if (!p) return <main className="page">Carregando gestão...</main>;
  const actions = p.management_actions || [];
  const issuedClient = (p as any).issued_client_snapshot || {};
  const address = client?.address_json || {};
  const contacts = client?.contacts_json || {};
  const archived = p.operational_state === 'arquivada';
  return (
    <main className="page manage-page">
      <header className="page-head" aria-hidden={Boolean(modal) || undefined} inert={modal ? true : undefined}>
        <div>
          <span className="eyebrow">GERENCIAR PROPOSTA</span>
          <h1>
            {p.code} · REV {String(p.revision).padStart(2, '0')}
          </h1>
          <p>
            {issuedClient.legal_name || issuedClient.name || p.client_legal_name || p.client_name} · {p.visible_state}
          </p>
        </div>
        <div className="row-actions">
          <Button variant="secondary" onClick={onBack}>
            Voltar à listagem
          </Button>
          <Button variant="ghost" onClick={toggleArchive} disabled={archiving}>
            {archiving ? 'Atualizando...' : archived ? 'Restaurar' : 'Arquivar'}
          </Button>
          <Button onClick={() => onOpen(id)}>Abrir</Button>
        </div>
      </header>
      {error && (
        <Notice tone="danger" title="Ação não concluída">
          {error}
        </Notice>
      )}
      {message && (
        <Notice tone="success" title="Atualizado">
          {message}
        </Notice>
      )}
      <section className="management-grid" aria-hidden={Boolean(modal) || undefined} inert={modal ? true : undefined}>
        <article className="management-card">
          <h2>Próxima ação</h2>
          <strong>{p.next_action}</strong>
          <p>Escolha uma ação compatível com o estado atual.</p>
          <div className="management-actions">
            {actions.map((a) => (
              <Button key={a} variant="secondary" onClick={() => setModal({ kind: 'management', value: a, title: labels[a] || a })}>
                {labels[a] || a}
              </Button>
            ))}
          </div>
        </article>
        <article className="management-card">
          <h2>Revisões e versões</h2>
          <p>A revisão emitida permanece preservada. Alterações posteriores criam uma nova revisão editável.</p>
          <Button
            variant="secondary"
            onClick={() => setModal({ kind: 'revision', value: 'price', title: 'Nova revisão por alteração de preço' })}
          >
            Nova revisão por preço
          </Button>
          <Button
            variant="secondary"
            onClick={() => setModal({ kind: 'revision', value: 'scope', title: 'Nova revisão por alteração de escopo' })}
          >
            Nova revisão por escopo
          </Button>
          <Button
            variant="secondary"
            onClick={() => setModal({ kind: 'revision', value: 'conditions', title: 'Nova revisão por prazo ou condições' })}
          >
            Nova revisão por prazo ou condições
          </Button>
          {p.visible_state.includes('aprov') && p.client_person_type === 'PF' && p.document_type === 'comercial' && (
            <Button onClick={() => setModal({ kind: 'contractual', value: 'contractual', title: 'Gerar versão contratual' })}>
              Gerar versão contratual
            </Button>
          )}
        </article>
        {client && (
          <article className="management-card full">
            <h2>Dados para contratação e assinatura</h2>
            <p>Contato principal, representante legal, signatário e responsável pelo pagamento permanecem papéis independentes.</p>
            <form className="contract-data-form" onSubmit={saveClient}>
              <div className="form-grid">
                <Field id="contract-tax" label={client.person_type === 'PF' ? 'CPF, quando disponível' : 'CNPJ'} required={client.person_type === 'PJ'}>
                  <input
                    id="contract-tax"
                    name="tax_id"
                    inputMode="numeric"
                    defaultValue={client.tax_id_formatted || client.tax_id}
                    onChange={(e) => {
                      e.currentTarget.value =
                        client.person_type === 'PF' ? formatCpf(e.currentTarget.value) : formatCnpj(e.currentTarget.value);
                    }}
                    required={client.person_type === 'PJ'}
                  />
                </Field>
                <Field id="contract-email" label="E-mail">
                  <input id="contract-email" name="email" type="email" defaultValue={client.email} />
                </Field>
                <Field id="contract-phone" label="Telefone">
                  <input id="contract-phone" name="phone" defaultValue={client.phone} />
                </Field>
                <Field id="contract-street" label="Endereço">
                  <input id="contract-street" name="street" defaultValue={address.street || ''} />
                </Field>
                <Field id="contract-number" label="Número">
                  <input id="contract-number" name="number" defaultValue={address.number || ''} />
                </Field>
                <Field id="contract-city" label="Cidade">
                  <input id="contract-city" name="city" defaultValue={address.city || ''} />
                </Field>
                <Field id="contract-state" label="UF">
                  <input id="contract-state" name="state" maxLength={2} defaultValue={address.state || ''} />
                </Field>
              </div>
              {client.person_type === 'PF' ? (
                <fieldset className="role-fieldset">
                  <legend>Contato principal</legend>
                  <div className="form-grid">
                    <Field id="primary-name" label="Nome">
                      <input id="primary-name" name="primary_name" defaultValue={contacts.primary?.name || client.name} />
                    </Field>
                    <Field id="primary-role" label="Função, quando aplicável">
                      <input id="primary-role" name="primary_role" defaultValue={contacts.primary?.role || ''} />
                    </Field>
                    <Field id="primary-email" label="E-mail">
                      <input id="primary-email" name="primary_email" type="email" defaultValue={contacts.primary?.email || client.email} />
                    </Field>
                    <Field id="primary-phone" label="Telefone">
                      <input id="primary-phone" name="primary_phone" defaultValue={contacts.primary?.phone || client.phone} />
                    </Field>
                  </div>
                </fieldset>
              ) : (
                <div className="role-grid-editor">
                  {contactKeys.map(([key, label]) => {
                    const c = contacts[key] || {};
                    return (
                      <fieldset className="role-fieldset" key={key}>
                        <legend>{label}</legend>
                        <Field id={`${key}-name`} label="Nome">
                          <input id={`${key}-name`} name={`${key}_name`} defaultValue={c.name || ''} />
                        </Field>
                        <Field id={`${key}-role`} label="Função ou cargo">
                          <input id={`${key}-role`} name={`${key}_role`} defaultValue={c.role || ''} />
                        </Field>
                        {key === 'signatory' && <Field id="signatory-tax-id" label="CPF do representante, opcional">
                          <input id="signatory-tax-id" name="signatory_tax_id" inputMode="numeric" defaultValue={c.tax_id ? formatCpf(c.tax_id) : ''} onChange={(e) => { e.currentTarget.value = formatCpf(e.currentTarget.value); }} />
                        </Field>}
                        <Field id={`${key}-email`} label="E-mail">
                          <input id={`${key}-email`} name={`${key}_email`} type="email" defaultValue={c.email || ''} />
                        </Field>
                        <Field id={`${key}-phone`} label="Telefone">
                          <input id={`${key}-phone`} name={`${key}_phone`} defaultValue={c.phone || ''} />
                        </Field>
                      </fieldset>
                    );
                  })}
                </div>
              )}
              <Button>Salvar dados do cliente</Button>
            </form>
          </article>
        )}
        <article className="management-card">
          <h2>Documentos</h2>
          {p.generations?.map((g: any) => (
            <div className="document-row" key={g.id}>
              <span>
                {g.generation_kind} · REV {String(g.revision).padStart(2, '0')}
              </span>
              <code>{g.pdf_hash || g.data_hash}</code>
            </div>
          ))}
          {Boolean(p.immutable) && (
            <a className="btn btn-secondary" href={api.fileUrl(`/proposals/${id}/issued-pdf`)} target="_blank" rel="noreferrer">
              Visualizar PDF emitido
            </a>
          )}
        </article>
        <article className="management-card">
          <h2>Assinatura</h2>
          <p>O documento assinado é preservado separadamente do PDF emitido.</p>
          <form onSubmit={signed}>
            <Field id="signed" label="Anexar PDF assinado">
              <input id="signed" name="signed" type="file" accept="application/pdf" />
            </Field>
            <Button variant="secondary">Registrar proposta assinada</Button>
          </form>
        </article>
        <article className="management-card full">
          <h2>Histórico</h2>
          <div className="timeline">
            {p.audit?.map((e: any) => (
              <div key={e.id}>
                <span>{new Date(e.created_at).toLocaleString('pt-BR')}</span>
                <strong>{e.event_type}</strong>
                <p>{e.reason || e.entity_type}</p>
              </div>
            ))}
          </div>
        </article>
      </section>
      {modal && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-labelledby="management-title">
          <form className="modal" onSubmit={submitModal}>
            <h2 id="management-title">{modal.title}</h2>
            <Field
              id="management-reason"
              label={modal.kind === 'management' ? 'Observação' : 'Motivo'}
              required={modal.kind !== 'management'}
            >
              <textarea id="management-reason" name="reason" required={modal.kind !== 'management'} />
            </Field>
            <div className="modal-actions">
              <Button type="button" variant="secondary" onClick={() => setModal(null)}>
                Cancelar
              </Button>
              <Button>Confirmar</Button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
